////////////////////
// ArcGraph Class //
////////////////////

/**
 * Class that generate an Arc Graph with one or more arcs
 * @param {number} width   width of the SVG arc graph canvas
 * @param {number} padding padding of the arc graph svg canvas
 * @prop {number} width width of the SVG arc graph canvas
 * @prop {number} height heig of the SVG arc graph canvas (same as width)
 * @prop {number} padding padding of the arc graph svg canvas
 * @prop {number} center x=y center of the arc graph
 * @prop {Array} arcs array of ArcPath instances in the arc graph
 * @prop {Element} svg Svg element of the arc graph
 */
export const ArcGraph = function(width, padding) {
  this.width = width || 500;
  this.height = this.width;
  this.center = this.width / 2;
  this.padding = padding || 0;
  this.arcs = [];
  this.svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  this.svg.setAttribute('viewBox', '0 0 ' + this.width + ' ' + this.height);
  this.svg.setAttribute('width', this.width);
  this.svg.setAttribute('height', this.width);
  this.svg.setAttribute('shape-rendering', 'geometricPrecision');
}

/**
 * Adds an new instance of ArcPath in this.arcs array
 * @param {object} arc Object with arc and svg attributes
 * @param {number} arc.percentage     Percentage of arc completion (0-100)
 * @param {number|false} arc.endMarker     Radius of circle at the end of the arc
 * @param {any} arc.ANY_CAMELCASED_SVG_ATTRIBUTE     camelCased SVG attribute that will be applied to arc path
 */
ArcGraph.prototype.addArc = function(arc) {
  if (Array.isArray(arc)) {
    arc.map(function(singleArc) {
      this.addArc(singleArc);
    }.bind(this))
  } else {
  	arc.padding = arc.padding || this.padding;
    var arcPath = new ArcPath(this.center, this.center, this.center, arc);
    if (arcPath) {
      this.arcs.push(arcPath);
    }
  }
}

/**
 * Changes stroke width of all ArcGraph's arcs
 * @param  {number} width new stroke width
 */
ArcGraph.prototype.changeStrokesWidth = function(width) {
  this.arcs.forEach(function(arc) {
    arc.changeStrokeWidth(width);
  })
}

/**
 * Returns the SVG element of the arc graph
 * @return {Element} SVG node element
 */
ArcGraph.prototype.getElement = function() {
  this._populateSVG();
  return this.svg;
}

/**
 * Appends the SVG element of the arc graph to specific node(s)
 * @param {string} selector seletor of the node where the SVG must be appended
 */
ArcGraph.prototype.appendTo = function(selector) {
  this._populateSVG();
  var nodes = document.querySelectorAll(selector);
  Array.prototype.forEach.call(nodes, function(element) {
    element.appendChild(this.svg);
  }.bind(this));
}

/**
 * Private method that appends ArcPath's SVG elements to ArcGraph SVG element
 */
ArcGraph.prototype._populateSVG = function() {
  this.svg.innerHTML = '';
  this.arcs.forEach(function(el) {
    this.svg.appendChild(el.getNode());
  }.bind(this));
}

///////////////////
// ArcPath Class //
///////////////////

/**
 * Abstraction of a percentage arc path
 * @constructor
 * @param {number} x      x position (in px) of the center of the arc
 * @param {number} y      y position (in px) of the center of the arc
 * @param {number} radius radius (in px) of the arc
 * @param {object} arc     object with arc attributes and svg attributes to be applied
 * @param {number} arc.percentage     Percentage of arc completion (0-100)
 * @param {number|false} arc.endMarker     Radius of circle at the end of the arc
 * @param {any} arc.ANY_CAMELCASED_SVG_ATTRIBUTE     camelCased SVG attribute that will be applied to arc path
 * @prop {Element} nodeGroup  Element *g* that will contain all arc svg elements
 * @prop {Element} node  Main svg element of the arc (*path* or *circle*)
 * @prop {number} x  x position (in px) of the center of the arc
 * @prop {number} y  y position (in px) of the center of the arc
 * @prop {number} radius  radius (in px) of the arc
 * @prop {object} data     object with arc attributes and svg attributes to be applied
 */
export const ArcPath = function(x, y, radius, arc) {
  if (!arc.stroke || !arc.strokeWidth || !arc.percentage) {
    console.error('Arc object is invalid: mandatory argument is missing');
    return false;
  } else {
    this.nodeGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    this.node = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    this.x = x || 250;
    this.y = y || 250;
    this.radius = radius || 250;
    this.data = arc;
    this._decorateNode();
  }
}

/**
 * Static array property that contains this.data object properties that will
 * not be considered as SVG attributes
 * @prop {array}
 * @memberof ArcPath
 * @static
 */
ArcPath.CUSTOM_PROPS = ['percentage', 'endMarker'];

/**
 * Static method that calculate path start and end point from center point and radius
 * @memberof ArcPath
 * @static
 * @param {number} x      x position (in px) of the center of the arc
 * @param {number} y      y position (in px) of the center of the arc
 * @param {number} radius radius (in px) of the arc
 * @param {number} percentage     Percentage of arc completion (0-100)
 * @return {string}            SVG path string that rappresents the arc
 */
ArcPath.arcPathFromCenter = function(x, y, radius, percentage) {
  var largeArc = percentage >= 50 ? '1' : '0';
  var start = ArcPath.getArcStart(x, y, radius, percentage);
  var end = ArcPath.getArcEnd(x, y, radius, percentage);
  return 'M' +
    start.x + ',' + start.y +
    ' A' + radius + ',' + radius +
    ' ' + '0 ' +
    largeArc +
    ' 1 ' +
    end.x + ',' + end.y;
}

/**
 * Static method that returns the start point of an arc path
 * @memberof ArcPath
 * @static
 * @param {number} x      x position (in px) of the center of the arc
 * @param {number} y      y position (in px) of the center of the arc
 * @param {number} radius radius (in px) of the arc
 * @param {number} percentage     Percentage of arc completion (0-100)
 * @return {object}            {x, y} object rappresenting the arc start point
 */
ArcPath.getArcStart = function(x, y, radius, percentage) {
	return {
    x: x,
    y: y - radius
  };
}

/**
 * Static method that returns the end point of an arc path
 * @memberof ArcPath
 * @static
 * @param {number} x      x position (in px) of the center of the arc
 * @param {number} y      y position (in px) of the center of the arc
 * @param {number} radius radius (in px) of the arc
 * @param {number} percentage     Percentage of arc completion (0-100)
 * @return {object}            {x, y} object rappresenting the arc end point
 */
ArcPath.getArcEnd = function(x, y, radius, percentage) {
  var angle = Math.PI * percentage / 50;
	return {
    x: x + Math.sin(angle) * radius,
    y: y - Math.cos(angle) * radius,
  };
}

/**
 * Returns SVG node group of the arc path
 * @public
 * @return {Element}  <g> node element
 */
ArcPath.prototype.getNode = function() {
  return this.nodeGroup;
}

/**
 * Changes arc stroke-width
 * @public
 * @param  {number} width  New stroke width of the arc
 */
ArcPath.prototype.changeStrokeWidth = function(width) {
  this.data.strokeWidth = width;
  this._decorateNode();
}

/**
 * Changes arc percentage completion
 * @public
 * @param  {number} value  New percentage to be applied to the arc
 */
ArcPath.prototype.setPercentage = function(value) {
  this.data.percentage = value;
  this._decorateNode();
}

/**
 * Private method that adds and decorate svg elements
 */
ArcPath.prototype._decorateNode = function() {
  this._setDefaults();
  if (this.data.percentage >= 100) {
    this._generateCircle();
  } else {
    this._generateArc();
  }
  this._setAttributes();
  this._appendNodes();
}

/**
 * Private method that creates a circle svg element
 */
ArcPath.prototype._generateCircle = function() {
  this.node = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  this.node.setAttribute('cx', this.x);
  this.node.setAttribute('cy', this.y);
  this.node.setAttribute('r', this._getSafeRadius());
}

/**
 * Private method that creates an arc path svg element
 */
ArcPath.prototype._generateArc = function() {
  this.node = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  this.node.setAttribute('d', ArcPath.arcPathFromCenter(this.x, this.y, this._getSafeRadius(), this.data.percentage));
}

/**
 * Private method that sets default arguments to main svg node
 */
ArcPath.prototype._setDefaults = function() {
  this.data.stroke = this.data.stroke || '#000000';
  this.data.strokeWidth = parseFloat(this.data.strokeWidth);
  this.data.fill = this.data.fill || 'none';
  this.data.strokeLinecap = this.data.strokeLinecap || 'butt';
}

/**
 * Private method that sets this.data object's arguments to main svg node
 */
ArcPath.prototype._setAttributes = function() {
  Object.keys(this.data).map(function(prop) {
    if (ArcPath.CUSTOM_PROPS.indexOf(prop) === -1) {
      var propName = prop.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
      this.node.setAttribute(propName, this.data[prop]);
    }
  }.bind(this));
}

/**
 * Private method that creates and returns wider white arc as bg for the main arc
 * (in order to prevent not pixel perfect overlapping of arcs)
 * @return {Element} SVG element of the arc bg
 */
ArcPath.prototype._createBgArc = function() {
  if (this.data.percentage < 100) {
    var bgArc = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    bgArc.setAttribute('d', ArcPath.arcPathFromCenter(this.x, this.y, this._getSafeRadius(), this.data.percentage));
  } else {
    var bgArc = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    this.node.setAttribute('cx', this.x);
    this.node.setAttribute('cy', this.y);
    this.node.setAttribute('r', this._getSafeRadius());
  }
  bgArc.setAttribute('fill', 'none');
  bgArc.setAttribute('stroke', 'white');
  bgArc.setAttribute('stroke-width', this.data.strokeWidth * 3);
  return bgArc;
}

/**
 * Private method that creates and returns the circle at the end of the arc path
 * @return {Element} SVG element of the end marker
 */
ArcPath.prototype._createEndMarker = function() {
	var maxMarkerSize = this.data.endMarker * 2.5 < this._getPadding() ? this.data.endMarker * 2.5 : this._getPadding();
	var endPoint = ArcPath.getArcEnd(this.x, this.y, this._getSafeRadius(), this.data.percentage);
  var endMarkerGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
	var endMarkerInner = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
	var endMarkerOuter = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  var endPosition = {
  	x: endPoint.x + this.data.strokeWidth * (endPoint.x > this.x ? -1/8 : 1/8),
  	y: endPoint.y + this.data.strokeWidth * (endPoint.y > this.y ? -1/8 : 1/8)
  }
  endMarkerInner.setAttribute('fill', this.data.stroke);
  endMarkerInner.setAttribute('stroke', 'white');
  endMarkerInner.setAttribute('stroke-width', this.data.endMarker * 0.4);
  endMarkerInner.setAttribute('cx', endPosition.x);
  endMarkerInner.setAttribute('cy', endPosition.y);
  endMarkerInner.setAttribute('r', this.data.endMarker);
  endMarkerOuter.setAttribute('fill', this.data.stroke);
  endMarkerOuter.setAttribute('fill-opacity', 0.3);
  endMarkerOuter.setAttribute('cx', endPosition.x);
  endMarkerOuter.setAttribute('cy', endPosition.y);
  endMarkerOuter.setAttribute('r', maxMarkerSize);
  endMarkerGroup.appendChild(endMarkerOuter);
  endMarkerGroup.appendChild(endMarkerInner);
  return endMarkerGroup;
}

/**
 * Private method that appends SVG nodes to main SVG node group
 */
ArcPath.prototype._appendNodes = function() {
	this.nodeGroup.innerHTML = '';
  this.nodeGroup.appendChild(this._createBgArc());
	this.nodeGroup.appendChild(this.node);
  if (this.data.endMarker) {
    this.nodeGroup.appendChild(this._createEndMarker());
  }
}

/**
 * Private method that returns the padding of the arc
 * (default: strokeWidth attribute)
 * @return {number}
 */
ArcPath.prototype._getPadding = function() {
	return this.data.padding || this.data.strokeWidth;
}

/**
 * Private method that returns safe radius that respects the padding
 * @return {number}
 */
ArcPath.prototype._getSafeRadius = function() {
  return this.radius - this._getPadding() / 2;
}



// WEBPACK FOOTER //
// ./src/layouts/general/arcGraph.js